/**
 * Filter storage manager for inline filter seen state
 * Handles sessionStorage operations with automatic cleanup and error recovery
 */

// Configuration
const STORAGE_KEY = 'inlineFilterSeen';
const MAX_AGE = 24 * 60 * 60 * 1000; // 24 hours
const CACHE_TTL = 5000; // 5 seconds cache TTL

// Cache for seen filters data
let cache = null;
let cacheTime = 0;

// Error logging utility
const logError = (operation, key, error) => {
    if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.warn(`SessionStorage ${operation} failed for key "${key}":`, error?.message || '');
    }
};

// Safe sessionStorage operations
const isStorageAvailable = () => typeof window !== 'undefined'
           && typeof window.sessionStorage !== 'undefined'
           && window.sessionStorage !== null;

const getStorageItem = (key, fallback = null) => {
    if (!isStorageAvailable()) return fallback;
    try {
        return window.sessionStorage.getItem(key);
    } catch (error) {
        logError('getItem', key, error);
        return fallback;
    }
};

const setStorageItem = (key, value) => {
    if (!isStorageAvailable()) return false;
    try {
        window.sessionStorage.setItem(key, value);
        return true;
    } catch (error) {
        logError('setItem', key, error);
        return false;
    }
};

const removeStorageItem = (key) => {
    if (!isStorageAvailable()) return false;
    try {
        window.sessionStorage.removeItem(key);
        return true;
    } catch (error) {
        logError('removeItem', key, error);
        return false;
    }
};

const getStorageSize = () => {
    if (!isStorageAvailable()) return 0;
    try {
        return Object.keys(window.sessionStorage).reduce((total, key) => total + window.sessionStorage[key].length + key.length, 0);
    } catch (error) {
        logError('getStorageSize', 'sessionStorage', error);
        // If we can't access storage to calculate size, it's effectively 0 for our purposes
        // This is a meaningful fallback for storage size calculation
        return 0;
    }
};

// Cache management
const updateCache = (data) => {
    cache = data;
    cacheTime = Date.now();
};

const invalidateCache = () => {
    cache = null;
    cacheTime = 0;
};

// Data processing
const migrateLegacyFormat = (arrayData) => {
    const timestamp = Date.now();
    return arrayData.reduce((acc, key) => {
        if (typeof key === 'string' && key.length > 0) {
            acc[key] = timestamp;
        }
        return acc;
    }, {});
};

const cleanupEntries = (seenFilters) => {
    const now = Date.now();

    const filtered = Object.entries(seenFilters)
        .filter(([key, timestamp]) => typeof key === 'string'
                   && typeof timestamp === 'number'
                   && (now - timestamp) < MAX_AGE);

    return Object.fromEntries(filtered);
};

const persistData = (data) => {
    try {
        return setStorageItem(STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
        logError('Failed to stringify data for storage', error);
        return false;
    }
};

const saveSeenFilters = (seenFilters) => {
    const cleaned = cleanupEntries(seenFilters);
    const success = persistData(cleaned);

    if (!success) {
        logError('Failed to persist filter data to storage');
    }

    updateCache(cleaned);
    return success;
};

// Core storage functions
export const getSeenFilters = () => {
    const now = Date.now();

    // Return cached data if still valid
    if (cache && (now - cacheTime) < CACHE_TTL) {
        return cache;
    }

    const rawData = getStorageItem(STORAGE_KEY, '{}');
    try {
        const data = JSON.parse(rawData || '{}');

        // Handle legacy array format migration
        if (Array.isArray(data)) {
            const migrated = migrateLegacyFormat(data);
            updateCache(migrated);
            persistData(migrated);
            return migrated;
        }

        // Auto-cleanup expired entries on read
        const cleaned = cleanupEntries(data);
        if (Object.keys(cleaned).length !== Object.keys(data).length) {
            persistData(cleaned);
        }

        updateCache(cleaned);
        return cleaned;
    } catch (error) {
        logError('Failed to parse seen filters data', error);
        const empty = {};
        updateCache(empty);
        return empty;
    }
};

export const hasSeenFilter = (sessionKey) => {
    if (!sessionKey || typeof sessionKey !== 'string') return false;
    const seenFilters = getSeenFilters();
    return sessionKey in seenFilters;
};

export const markFilterAsSeen = (sessionKey) => {
    if (!sessionKey || typeof sessionKey !== 'string') return false;

    const seenFilters = { ...getSeenFilters() };
    seenFilters[sessionKey] = Date.now();

    return saveSeenFilters(seenFilters);
};

export const clearFilterSeenData = () => {
    invalidateCache();
    return removeStorageItem(STORAGE_KEY);
};

export const getAllSeenFilters = () => getSeenFilters();

export const getStorageStats = () => {
    const seenFilters = getSeenFilters();
    const count = Object.keys(seenFilters).length;

    return {
        filterCount: count,
        totalStorageSize: getStorageSize(),
        cacheHit: cache !== null,
        config: { key: STORAGE_KEY, maxAge: MAX_AGE },
    };
};

export const cleanupExpiredFilters = () => {
    const seenFilters = getSeenFilters();
    const cleaned = cleanupEntries(seenFilters);
    const wasCleanupNeeded = Object.keys(cleaned).length !== Object.keys(seenFilters).length;

    if (wasCleanupNeeded) {
        return saveSeenFilters(cleaned);
    }
    return true;
};
